<div class="share-viewer-container">
  <!-- Branding Header -->
  <header class="share-header">
    <div class="brand">
      <h1 class="brand-name">🚀 Taptimize</h1>
      <p class="brand-tagline">SEO analysis &amp; competitive intelligence</p>
    </div>
  </header>

  <!-- Loading State -->
  <div class="state-container" *ngIf="loading">
    <div class="loading-spinner"></div>
    <p class="state-message">Loading shared content…</p>
  </div>

  <!-- Error / Expired / Revoked State -->
  <div class="state-container state-error" *ngIf="!loading && error">
    <div class="error-icon">🔗</div>
    <h2>Link Unavailable</h2>
    <p class="state-message">{{ error }}</p>
    <a routerLink="/login" class="cta-btn cta-secondary">Sign in to Taptimize</a>
  </div>

  <!-- ══════════════════════════════════════════════════════════ -->
  <!-- DOMAIN OVERVIEW VIEW                                      -->
  <!-- ══════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="!loading && !error && domainData">
    <section class="share-content">
      <div class="content-title-section">
        <h2 class="content-title">Domain Topic Authority</h2>
        <p class="content-subtitle">
          <span class="domain-badge">{{ domainData.domain }}</span>
          <span *ngIf="domainData.label" class="content-label">{{ domainData.label }}</span>
        </p>
        <p class="content-meta">{{ domainData.parentTopics.length }} topics analyzed</p>
      </div>

      <!-- Topic Cards Grid -->
      <div class="topics-grid">
        <div *ngFor="let topic of domainData.parentTopics" class="topic-card">
          <div class="topic-card-header">
            <h3 class="topic-label">{{ topic.label }}</h3>
            <span class="tier-badge" [ngClass]="getTierClass(topic.topicTier)">
              {{ getTierLabel(topic.topicTier) }}
            </span>
          </div>

          <div class="authority-score-box">
            <span class="authority-value" [style.color]="getScoreColor(topic.authorityScore)">
              {{ topic.authorityScore.toFixed(1) }}
            </span>
            <span class="authority-label">Authority</span>
          </div>

          <div class="metrics-mini-grid">
            <div class="mini-metric">
              <span class="mini-label">Breadth</span>
              <span class="mini-value">{{ (topic.breadthScore * 100).toFixed(0) }}%</span>
              <div class="mini-bar"><div class="mini-bar-fill" [style.width]="getScoreBarWidth(topic.breadthScore * 100)" [style.background-color]="getScoreColor(topic.breadthScore * 100)"></div></div>
            </div>
            <div class="mini-metric">
              <span class="mini-label">Depth</span>
              <span class="mini-value">{{ (topic.depthScore * 100).toFixed(0) }}%</span>
              <div class="mini-bar"><div class="mini-bar-fill" [style.width]="getScoreBarWidth(topic.depthScore * 100)" [style.background-color]="getScoreColor(topic.depthScore * 100)"></div></div>
            </div>
            <div class="mini-metric">
              <span class="mini-label">Coverage</span>
              <span class="mini-value">{{ (topic.coverageScore * 100).toFixed(0) }}%</span>
              <div class="mini-bar"><div class="mini-bar-fill" [style.width]="getScoreBarWidth(topic.coverageScore * 100)" [style.background-color]="getScoreColor(topic.coverageScore * 100)"></div></div>
            </div>
            <div class="mini-metric">
              <span class="mini-label">Strength</span>
              <span class="mini-value">{{ (topic.strengthScore * 100).toFixed(0) }}%</span>
              <div class="mini-bar"><div class="mini-bar-fill" [style.width]="getScoreBarWidth(topic.strengthScore * 100)" [style.background-color]="getScoreColor(topic.strengthScore * 100)"></div></div>
            </div>
            <div class="mini-metric">
              <span class="mini-label">Focus</span>
              <span class="mini-value">{{ (topic.focusScore * 100).toFixed(0) }}%</span>
              <div class="mini-bar"><div class="mini-bar-fill" [style.width]="getScoreBarWidth(topic.focusScore * 100)" [style.background-color]="getScoreColor(topic.focusScore * 100)"></div></div>
            </div>
            <div class="mini-metric" *ngIf="topic.linkScore">
              <span class="mini-label">Links</span>
              <span class="mini-value">{{ (topic.linkScore * 100).toFixed(0) }}%</span>
              <div class="mini-bar"><div class="mini-bar-fill" [style.width]="getScoreBarWidth(topic.linkScore * 100)" [style.background-color]="getScoreColor(topic.linkScore * 100)"></div></div>
            </div>
          </div>

          <div class="topic-card-stats">
            <span>{{ topic.pageCount }} pages</span>
            <span>Avg weight {{ topic.averageWeight.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </section>
  </ng-container>

  <!-- ══════════════════════════════════════════════════════════ -->
  <!-- COMPARISON VIEW                                           -->
  <!-- ══════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="!loading && !error && comparisonData">
    <section class="share-content">
      <div class="content-title-section">
        <h2 class="content-title">Page Comparison</h2>
        <p *ngIf="comparisonData.label" class="content-label">{{ comparisonData.label }}</p>
        <p class="content-meta">
          Compared on {{ formatDate(comparisonData.execution.startedAt) }}
        </p>
      </div>

      <!-- ─── Diagnosis Banner ("Why They Probably Win") ─── -->
      <div class="diagnosis-banner"
           *ngIf="getDiagnosisBullets().length > 0"
           [class.competitor-advantage]="hasCompetitorAdvantages() && !hasYourAdvantages()"
           [class.your-advantage]="hasYourAdvantages() && !hasCompetitorAdvantages()"
           [class.mixed-results]="hasCompetitorAdvantages() && hasYourAdvantages()">
        <div class="diagnosis-header">
          <span class="diagnosis-icon" *ngIf="hasCompetitorAdvantages() && !hasYourAdvantages()">⚠️</span>
          <span class="diagnosis-icon" *ngIf="hasYourAdvantages() && !hasCompetitorAdvantages()">✅</span>
          <span class="diagnosis-icon" *ngIf="hasCompetitorAdvantages() && hasYourAdvantages()">📊</span>
          <h3 *ngIf="hasCompetitorAdvantages() && !hasYourAdvantages()">Why They Probably Win</h3>
          <h3 *ngIf="hasYourAdvantages() && !hasCompetitorAdvantages()">Your Competitive Advantages</h3>
          <h3 *ngIf="hasCompetitorAdvantages() && hasYourAdvantages()">Competitive Analysis</h3>
        </div>
        <ul class="diagnosis-list">
          <li *ngFor="let bullet of getDiagnosisBullets()"
              [class.competitor-wins]="bullet.winner === 'competitor'"
              [class.you-win]="bullet.winner === 'you'">
            <span class="bullet-icon">
              <span *ngIf="bullet.winner === 'competitor'">−</span>
              <span *ngIf="bullet.winner === 'you'">+</span>
            </span>
            <span class="bullet-text">{{ bullet.text }}</span>
          </li>
        </ul>
        <p class="diagnosis-note">
          Based on keyword positions, link structure, content freshness, and domain topic authority.
          <br>This analysis evolves as more data becomes available.
        </p>
      </div>

      <!-- ─── Shared Entity Coverage ─── -->
      <div class="topic-share-coverage-section" *ngIf="hasTopicShareCoverage()">
        <div class="topic-share-coverage-card">
          <div class="coverage-header">
            <h3>
              <span class="header-icon">📊</span>
              Shared Entity Coverage
              <span class="info-tooltip" title="Compares how many shared topic entities each domain covers based on Topic Share analysis.">ⓘ</span>
            </h3>
            <span class="coverage-topic" *ngIf="topicShareCoverage?.topic">
              Topic: <strong>{{ topicShareCoverage!.topic }}</strong>
            </span>
          </div>

          <div class="coverage-content" *ngIf="topicShareCoverage?.available">
            <div class="coverage-comparison">
              <!-- Your Domain -->
              <div class="coverage-domain your-domain" *ngIf="getYourDomainCoverage() as yourCov">
                <div class="domain-header">
                  <span class="domain-label">YOU</span>
                  <span class="domain-name">{{ yourCov.domain }}</span>
                </div>
                <div class="coverage-percentage">
                  <span class="percentage-value">{{ yourCov.coveragePercentage | number:'1.0-0' }}%</span>
                  <span class="percentage-detail">{{ yourCov.entitiesCovered }} of {{ yourCov.totalEntities }} entities</span>
                </div>
                <div class="coverage-bar">
                  <div class="coverage-fill your-fill" [style.width.%]="yourCov.coveragePercentage"></div>
                </div>
              </div>

              <!-- Competitor Domain -->
              <div class="coverage-domain competitor-domain" *ngIf="getCompetitorDomainCoverage() as compCov">
                <div class="domain-header">
                  <span class="domain-label">COMPETITOR</span>
                  <span class="domain-name">{{ compCov.domain }}</span>
                </div>
                <div class="coverage-percentage">
                  <span class="percentage-value">{{ compCov.coveragePercentage | number:'1.0-0' }}%</span>
                  <span class="percentage-detail">{{ compCov.entitiesCovered }} of {{ compCov.totalEntities }} entities</span>
                </div>
                <div class="coverage-bar">
                  <div class="coverage-fill competitor-fill" [style.width.%]="compCov.coveragePercentage"></div>
                </div>
              </div>
            </div>

            <!-- Top URLs toggle -->
            <div class="coverage-urls-section"
                 *ngIf="(getYourDomainCoverage()?.topUrls?.length || 0) > 0 || (getCompetitorDomainCoverage()?.topUrls?.length || 0) > 0">
              <button class="urls-toggle-btn" (click)="toggleTopicShareUrls()">
                {{ topicShareUrlsExpanded ? '▼' : '▶' }} Top Ranking URLs
                <span class="urls-count">({{ (getYourDomainCoverage()?.topUrls?.length || 0) + (getCompetitorDomainCoverage()?.topUrls?.length || 0) }})</span>
              </button>

              <div class="urls-content" *ngIf="topicShareUrlsExpanded">
                <div class="urls-list your-urls" *ngIf="getYourDomainCoverage()?.topUrls?.length">
                  <h4>Your Top URLs</h4>
                  <ul>
                    <li *ngFor="let url of getYourDomainCoverage()!.topUrls">
                      <a [href]="url.url" target="_blank" rel="noopener" class="url-link">{{ url.title || url.url }}</a>
                      <span class="url-entities">({{ url.entitiesCount }} entities)</span>
                    </li>
                  </ul>
                </div>
                <div class="urls-list competitor-urls" *ngIf="getCompetitorDomainCoverage()?.topUrls?.length">
                  <h4>Competitor Top URLs</h4>
                  <ul>
                    <li *ngFor="let url of getCompetitorDomainCoverage()!.topUrls">
                      <a [href]="url.url" target="_blank" rel="noopener" class="url-link">{{ url.title || url.url }}</a>
                      <span class="url-entities">({{ url.entitiesCount }} entities)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="coverage-meta" *ngIf="topicShareCoverage?.lastUpdated">
              <span class="meta-text">Topic Share data from {{ formatDate(topicShareCoverage!.lastUpdated) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── Side-by-side Page Columns ─── -->
      <div class="comparison-grid">
        <!-- Your Page -->
        <div class="comparison-column">
          <h3 class="column-heading your-page">Your Page</h3>
          <a class="page-url" [href]="comparisonData.yourPage.url" target="_blank" rel="noopener">
            {{ comparisonData.yourPage.url }}
          </a>

          <ng-container *ngIf="comparisonData.yourPage.analysis as a">
            <!-- Core Metrics -->
            <div class="metric-card">
              <span class="mc-label">Page Title</span>
              <span class="mc-value">{{ getPageTitle(a) }}</span>
            </div>
            <div class="metric-card">
              <span class="mc-label">OnPage Score</span>
              <span class="mc-value mc-score" [ngClass]="getScoreClass(getOnPageScore(a))">
                {{ getOnPageScore(a) ?? 'N/A' }}
              </span>
            </div>
            <div class="metrics-row">
              <div class="metric-card compact">
                <span class="mc-label">Word Count</span>
                <span class="mc-value">{{ getWordCount(a)?.toLocaleString() ?? 'N/A' }}</span>
              </div>
              <div class="metric-card compact">
                <span class="mc-label">Status Code</span>
                <span class="mc-value" [ngClass]="getStatusCodeClass(getStatusCode(a))">
                  {{ getStatusCode(a) ?? 'N/A' }}
                </span>
              </div>
            </div>

            <!-- Content Freshness -->
            <div class="metric-card" *ngIf="getLastContentModified(a)">
              <span class="mc-label">Last Updated</span>
              <span class="mc-value">
                {{ formatDate(getLastContentModified(a)) }}
                <small class="freshness-source" *ngIf="getLastContentModifiedSource(a)">
                  ({{ getFreshnessLabel(getLastContentModifiedSource(a)) }})
                </small>
              </span>
            </div>

            <!-- Heading Structure -->
            <div class="metric-card" *ngIf="getH1Count(a) > 0 || getH2Count(a) > 0 || getH3Count(a) > 0">
              <span class="mc-label">Heading Structure</span>
              <div class="heading-counts">
                <span class="heading-chip">H1: {{ getH1Count(a) }}</span>
                <span class="heading-chip">H2: {{ getH2Count(a) }}</span>
                <span class="heading-chip">H3: {{ getH3Count(a) }}</span>
              </div>
            </div>

            <!-- Readability -->
            <div class="metric-card" *ngIf="getReadabilityIndex(a) !== null">
              <span class="mc-label">Readability Index</span>
              <span class="mc-value">{{ getReadabilityIndex(a) }}</span>
            </div>

            <!-- Topic Signals -->
            <div class="metric-card">
              <span class="mc-label">Topic Signals</span>
              <span class="mc-value">{{ getTopicSignalsCount(a) }}</span>
            </div>
            <div class="metric-card" *ngIf="getTopicSignalsCount(a) > 0">
              <span class="mc-label">Top Topics</span>
              <div class="topic-chips">
                <span class="topic-chip" *ngFor="let sig of getTopicSignals(a).slice(0, 8)">
                  {{ sig.label }} <small>({{ sig.weight?.toFixed(2) }})</small>
                </span>
              </div>
            </div>

            <!-- Internal Links -->
            <div class="metric-card">
              <span class="mc-label">Internal Links</span>
              <span class="mc-value">{{ getInternalLinksCount(a) }}</span>
              <div class="link-breakdown" *ngIf="getInternalLinksCount(a) > 0">
                <span class="link-type contextual">{{ getContextualLinksCount(a) }} contextual</span>
                <span class="link-type structural">{{ getStructuralLinksCount(a) }} structural</span>
              </div>
            </div>

            <!-- Critical Issues -->
            <div class="metric-card issues-card" *ngIf="getCriticalIssues(a).length > 0">
              <span class="mc-label">Critical Issues ({{ getCriticalIssues(a).length }})</span>
              <ul class="issues-list">
                <li *ngFor="let issue of getCriticalIssues(a).slice(0, 5)">{{ issue }}</li>
              </ul>
              <span *ngIf="getCriticalIssues(a).length > 5" class="more-items">
                +{{ getCriticalIssues(a).length - 5 }} more
              </span>
            </div>

            <!-- Ranked Keywords -->
            <div class="metric-card">
              <span class="mc-label">Ranked Keywords</span>
              <span class="mc-value">{{ getRankedKeywordsCount(a) }}</span>
            </div>
            <div class="ranked-keywords-list" *ngIf="getRankedKeywords(a).length > 0">
              <table class="keywords-table">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Pos</th>
                    <th>Vol</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let kw of getRankedKeywords(a).slice(0, 15)">
                    <td class="kw-keyword">{{ kw.keyword }}</td>
                    <td class="kw-pos">{{ kw.position }}</td>
                    <td class="kw-vol">{{ kw.searchVolume?.toLocaleString() || '–' }}</td>
                  </tr>
                </tbody>
              </table>
              <span *ngIf="getRankedKeywords(a).length > 15" class="more-items">
                +{{ getRankedKeywords(a).length - 15 }} more keywords
              </span>
            </div>
          </ng-container>

          <ng-container *ngIf="!comparisonData.yourPage.analysis">
            <p class="no-data-msg">Analysis data not available</p>
          </ng-container>
        </div>

        <!-- Competitor Page -->
        <div class="comparison-column">
          <h3 class="column-heading competitor-page">Competitor Page</h3>
          <a class="page-url" [href]="comparisonData.competitorPage.url" target="_blank" rel="noopener">
            {{ comparisonData.competitorPage.url }}
          </a>

          <ng-container *ngIf="comparisonData.competitorPage.analysis as a">
            <!-- Core Metrics -->
            <div class="metric-card">
              <span class="mc-label">Page Title</span>
              <span class="mc-value">{{ getPageTitle(a) }}</span>
            </div>
            <div class="metric-card">
              <span class="mc-label">OnPage Score</span>
              <span class="mc-value mc-score" [ngClass]="getScoreClass(getOnPageScore(a))">
                {{ getOnPageScore(a) ?? 'N/A' }}
              </span>
            </div>
            <div class="metrics-row">
              <div class="metric-card compact">
                <span class="mc-label">Word Count</span>
                <span class="mc-value">{{ getWordCount(a)?.toLocaleString() ?? 'N/A' }}</span>
              </div>
              <div class="metric-card compact">
                <span class="mc-label">Status Code</span>
                <span class="mc-value" [ngClass]="getStatusCodeClass(getStatusCode(a))">
                  {{ getStatusCode(a) ?? 'N/A' }}
                </span>
              </div>
            </div>

            <!-- Content Freshness -->
            <div class="metric-card" *ngIf="getLastContentModified(a)">
              <span class="mc-label">Last Updated</span>
              <span class="mc-value">
                {{ formatDate(getLastContentModified(a)) }}
                <small class="freshness-source" *ngIf="getLastContentModifiedSource(a)">
                  ({{ getFreshnessLabel(getLastContentModifiedSource(a)) }})
                </small>
              </span>
            </div>

            <!-- Heading Structure -->
            <div class="metric-card" *ngIf="getH1Count(a) > 0 || getH2Count(a) > 0 || getH3Count(a) > 0">
              <span class="mc-label">Heading Structure</span>
              <div class="heading-counts">
                <span class="heading-chip">H1: {{ getH1Count(a) }}</span>
                <span class="heading-chip">H2: {{ getH2Count(a) }}</span>
                <span class="heading-chip">H3: {{ getH3Count(a) }}</span>
              </div>
            </div>

            <!-- Readability -->
            <div class="metric-card" *ngIf="getReadabilityIndex(a) !== null">
              <span class="mc-label">Readability Index</span>
              <span class="mc-value">{{ getReadabilityIndex(a) }}</span>
            </div>

            <!-- Topic Signals -->
            <div class="metric-card">
              <span class="mc-label">Topic Signals</span>
              <span class="mc-value">{{ getTopicSignalsCount(a) }}</span>
            </div>
            <div class="metric-card" *ngIf="getTopicSignalsCount(a) > 0">
              <span class="mc-label">Top Topics</span>
              <div class="topic-chips">
                <span class="topic-chip" *ngFor="let sig of getTopicSignals(a).slice(0, 8)">
                  {{ sig.label }} <small>({{ sig.weight?.toFixed(2) }})</small>
                </span>
              </div>
            </div>

            <!-- Internal Links -->
            <div class="metric-card">
              <span class="mc-label">Internal Links</span>
              <span class="mc-value">{{ getInternalLinksCount(a) }}</span>
              <div class="link-breakdown" *ngIf="getInternalLinksCount(a) > 0">
                <span class="link-type contextual">{{ getContextualLinksCount(a) }} contextual</span>
                <span class="link-type structural">{{ getStructuralLinksCount(a) }} structural</span>
              </div>
            </div>

            <!-- Critical Issues -->
            <div class="metric-card issues-card" *ngIf="getCriticalIssues(a).length > 0">
              <span class="mc-label">Critical Issues ({{ getCriticalIssues(a).length }})</span>
              <ul class="issues-list">
                <li *ngFor="let issue of getCriticalIssues(a).slice(0, 5)">{{ issue }}</li>
              </ul>
              <span *ngIf="getCriticalIssues(a).length > 5" class="more-items">
                +{{ getCriticalIssues(a).length - 5 }} more
              </span>
            </div>

            <!-- Ranked Keywords -->
            <div class="metric-card">
              <span class="mc-label">Ranked Keywords</span>
              <span class="mc-value">{{ getRankedKeywordsCount(a) }}</span>
            </div>
            <div class="ranked-keywords-list" *ngIf="getRankedKeywords(a).length > 0">
              <table class="keywords-table">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Pos</th>
                    <th>Vol</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let kw of getRankedKeywords(a).slice(0, 15)">
                    <td class="kw-keyword">{{ kw.keyword }}</td>
                    <td class="kw-pos">{{ kw.position }}</td>
                    <td class="kw-vol">{{ kw.searchVolume?.toLocaleString() || '–' }}</td>
                  </tr>
                </tbody>
              </table>
              <span *ngIf="getRankedKeywords(a).length > 15" class="more-items">
                +{{ getRankedKeywords(a).length - 15 }} more keywords
              </span>
            </div>
          </ng-container>

          <ng-container *ngIf="!comparisonData.competitorPage.analysis">
            <p class="no-data-msg">Analysis data not available</p>
          </ng-container>
        </div>
      </div>

      <!-- ─── Keyword Overlap & Opportunities ─── -->
      <div class="keyword-overlap-section" *ngIf="computeKeywordOverlap() as overlap">
        <h3 class="section-title">Keyword Overlap &amp; Opportunities</h3>

        <div class="overlap-summary">
          <div class="overlap-stat shared">
            <div class="stat-label">Shared Keywords</div>
            <div class="stat-value">{{ overlap.sharedCount }}</div>
            <div class="stat-detail" *ngIf="overlap.sharedCount > 0">
              Your avg pos: <strong>{{ overlap.sharedAvgPositionYour }}</strong> ·
              Competitor avg pos: <strong>{{ overlap.sharedAvgPositionCompetitor }}</strong>
            </div>
          </div>
          <div class="overlap-stat your-unique">
            <div class="stat-label">Your Unique Keywords</div>
            <div class="stat-value">{{ overlap.yourUniqueCount }}</div>
          </div>
          <div class="overlap-stat competitor-unique">
            <div class="stat-label">Competitor Unique Keywords</div>
            <div class="stat-value">{{ overlap.competitorUniqueCount }}</div>
          </div>
        </div>

        <!-- Content Opportunities -->
        <div class="content-opportunities" *ngIf="overlap.competitorUniqueCount > 0">
          <h4>Content Opportunities (keywords they rank for, you don't)</h4>
          <div class="opportunity-chips">
            <span class="opportunity-chip" *ngFor="let kw of overlap.competitorUniqueKeywords.slice(0, 20)">
              {{ kw.keyword }}
              <small *ngIf="kw.searchVolume"> ({{ kw.searchVolume.toLocaleString() }})</small>
              <small class="kw-pos-badge">pos {{ kw.position }}</small>
            </span>
          </div>
          <span *ngIf="overlap.competitorUniqueCount > 20" class="more-items">
            +{{ overlap.competitorUniqueCount - 20 }} more
          </span>
        </div>
      </div>

    </section>
  </ng-container>

  <!-- ══════════════════════════════════════════════════════════ -->
  <!-- PAGE ANALYSIS VIEW                                        -->
  <!-- ══════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="!loading && !error && pageAnalysisData">
    <section class="share-content">
      <div class="content-title-section">
        <h2 class="content-title">Page Analysis</h2>
        <p *ngIf="pageAnalysisData.label" class="content-label">{{ pageAnalysisData.label }}</p>
        <a *ngIf="pageAnalysisData.url" class="page-url title-url" [href]="pageAnalysisData.url" target="_blank" rel="noopener">
          {{ pageAnalysisData.url }}
        </a>
      </div>

      <ng-container *ngIf="pageAnalysisData.analysis as a">
        <div class="analysis-metrics">
          <div class="metric-card">
            <span class="mc-label">Page Title</span>
            <span class="mc-value">{{ getPageTitle(a) }}</span>
          </div>
          <div class="metric-card">
            <span class="mc-label">OnPage Score</span>
            <span class="mc-value mc-score" [ngClass]="getScoreClass(getOnPageScore(a))">
              {{ getOnPageScore(a) ?? 'N/A' }}
            </span>
          </div>
          <div class="metric-card">
            <span class="mc-label">Word Count</span>
            <span class="mc-value">{{ getWordCount(a)?.toLocaleString() ?? 'N/A' }}</span>
          </div>
          <div class="metric-card">
            <span class="mc-label">Status Code</span>
            <span class="mc-value" [ngClass]="getStatusCodeClass(getStatusCode(a))">
              {{ getStatusCode(a) ?? 'N/A' }}
            </span>
          </div>
          <div class="metric-card" *ngIf="getLastContentModified(a)">
            <span class="mc-label">Last Updated</span>
            <span class="mc-value">{{ formatDate(getLastContentModified(a)) }}</span>
          </div>
          <div class="metric-card" *ngIf="getReadabilityIndex(a) !== null">
            <span class="mc-label">Readability Index</span>
            <span class="mc-value">{{ getReadabilityIndex(a) }}</span>
          </div>
          <div class="metric-card" *ngIf="getH1Count(a) > 0 || getH2Count(a) > 0 || getH3Count(a) > 0">
            <span class="mc-label">Heading Structure</span>
            <div class="heading-counts">
              <span class="heading-chip">H1: {{ getH1Count(a) }}</span>
              <span class="heading-chip">H2: {{ getH2Count(a) }}</span>
              <span class="heading-chip">H3: {{ getH3Count(a) }}</span>
            </div>
          </div>
          <div class="metric-card">
            <span class="mc-label">Topic Signals</span>
            <span class="mc-value">{{ getTopicSignalsCount(a) }}</span>
          </div>
          <div class="metric-card" *ngIf="getTopicSignalsCount(a) > 0">
            <span class="mc-label">Top Topics</span>
            <div class="topic-chips">
              <span class="topic-chip" *ngFor="let sig of getTopicSignals(a).slice(0, 8)">
                {{ sig.label }} <small>({{ sig.weight?.toFixed(2) }})</small>
              </span>
            </div>
          </div>
          <div class="metric-card">
            <span class="mc-label">Internal Links</span>
            <span class="mc-value">{{ getInternalLinksCount(a) }}</span>
            <div class="link-breakdown" *ngIf="getInternalLinksCount(a) > 0">
              <span class="link-type contextual">{{ getContextualLinksCount(a) }} contextual</span>
              <span class="link-type structural">{{ getStructuralLinksCount(a) }} structural</span>
            </div>
          </div>
          <div class="metric-card">
            <span class="mc-label">Ranked Keywords</span>
            <span class="mc-value">{{ getRankedKeywordsCount(a) }}</span>
          </div>
          <div class="metric-card issues-card" *ngIf="getCriticalIssues(a).length > 0">
            <span class="mc-label">Critical Issues ({{ getCriticalIssues(a).length }})</span>
            <ul class="issues-list">
              <li *ngFor="let issue of getCriticalIssues(a).slice(0, 5)">{{ issue }}</li>
            </ul>
          </div>
        </div>

        <!-- Ranked Keywords Table -->
        <div class="ranked-keywords-section" *ngIf="getRankedKeywords(a).length > 0">
          <h3 class="section-title">Ranked Keywords</h3>
          <table class="keywords-table standalone">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Position</th>
                <th>Volume</th>
                <th>KD</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let kw of getRankedKeywords(a).slice(0, 20)">
                <td class="kw-keyword">{{ kw.keyword }}</td>
                <td class="kw-pos">{{ kw.position }}</td>
                <td class="kw-vol">{{ kw.searchVolume?.toLocaleString() || '–' }}</td>
                <td class="kw-kd">{{ kw.keywordDifficulty ?? '–' }}</td>
              </tr>
            </tbody>
          </table>
          <span *ngIf="getRankedKeywords(a).length > 20" class="more-items">
            +{{ getRankedKeywords(a).length - 20 }} more keywords
          </span>
        </div>
      </ng-container>
      <ng-template #noPageAnalysis>
        <p class="no-data-msg">Analysis data not available</p>
      </ng-template>
    </section>
  </ng-container>

  <!-- CTA Footer -->
  <footer class="share-footer" *ngIf="!loading && !error && shareData">
    <div class="cta-section">
      <h3>Want deeper analysis?</h3>
      <p>Create a free Taptimize account to compare pages, analyze domains, and uncover competitive advantages.</p>
      <div class="cta-buttons">
        <a routerLink="/register" class="cta-btn cta-primary">Create Free Account</a>
        <a routerLink="/login" class="cta-btn cta-secondary">Sign In</a>
      </div>
    </div>
  </footer>
</div>
